import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  HeartPulse,
  Users,
  Baby,
  UserRound,
  ShieldAlert,
  Stethoscope,
} from "lucide-react";
import { Button } from "../components/ui/button";

const audience = [
  { icon: Baby, label: "Crianças" },
  { icon: Users, label: "Adolescentes" },
  { icon: UserRound, label: "Adultos" },
  { icon: HeartPulse, label: "Idosos" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#F0FDF4] via-white to-white" />
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-14 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
          <div>
            <span
              className="inline-flex items-center gap-2 rounded-full border border-[#166534]/15 bg-[#F0FDF4] text-[#166534] text-xs font-semibold px-3 py-1.5"
              data-testid="hero-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#166534]" />
              Programa de benefícios · R$ 30,00/mês
            </span>
            <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.02] tracking-tighter">
              Cuidar de quem você ama, <br />
              <span className="text-[#166534]">com propósito</span> e acessível.
            </h1>
            <p className="mt-5 text-base md:text-lg text-neutral-700 max-w-xl leading-relaxed">
              O Ressignificar Benefícios conecta crianças, adolescentes, adultos e idosos a uma
              rede de profissionais com valores especiais. Uma mensalidade fixa, sem surpresas.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/adesao">
                <Button
                  data-testid="hero-cta-signup"
                  className="rounded-full bg-[#166534] hover:bg-[#14532d] hover:text-white text-white px-6 h-12 text-base"
                >
                  Quero fazer parte
                  <ArrowRight className="ml-1 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/beneficios">
                <Button
                  variant="outline"
                  data-testid="hero-cta-benefits"
                  className="rounded-full h-12 px-6 border-[#166534]/30 text-[#166534] hover:bg-[#F0FDF4] hover:text-[#14532d]"
                >
                  Ver benefícios
                </Button>
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xl">
              {audience.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="rounded-2xl border border-neutral-200 p-4 bg-white hover:-translate-y-1 transition-transform"
                  data-testid={`audience-${label.toLowerCase()}`}
                >
                  <Icon className="w-5 h-5 text-[#166534]" />
                  <p className="mt-2 text-sm font-semibold">{label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-[32px] overflow-hidden border border-neutral-200 shadow-xl shadow-[#166534]/10">
              <img
                src="https://images.pexels.com/photos/18857314/pexels-photo-18857314.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Família feliz ao ar livre"
                className="w-full h-full object-cover aspect-[4/5]"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 rounded-2xl bg-white border border-neutral-200 shadow-lg p-4 w-56 hidden sm:block">
              <p className="text-xs text-neutral-500 font-medium">Mensalidade única</p>
              <p className="font-display text-2xl font-extrabold text-[#166534]">R$ 30,00</p>
              <p className="text-xs text-neutral-500 mt-1">Família inteira, benefícios reais.</p>
            </div>
          </div>
        </div>
      </section>

      {/* IMPORTANT NOTICE */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-6">
        <div
          className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex gap-3 items-start"
          data-testid="legal-disclaimer"
        >
          <ShieldAlert className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900 leading-relaxed">
            <strong className="font-semibold">Importante:</strong> O Ressignificar Benefícios{" "}
            <strong>não é plano de saúde</strong>, <strong>não é convênio médico</strong> e{" "}
            <strong>não oferece cobertura de procedimentos</strong>. As consultas são cobradas
            separadamente, com valores especiais negociados pelo programa.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="grid md:grid-cols-[0.9fr_1.1fr] gap-10 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-[#166534]">COMO FUNCIONA</p>
            <h2 className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
              Simples, transparente e feito para a vida real.
            </h2>
            <p className="mt-4 text-neutral-700 leading-relaxed">
              Faça sua adesão em minutos. Após a aprovação, você passa a ter acesso a valores
              especiais com profissionais parceiros.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            {[
              {
                step: "01",
                title: "Faça sua adesão",
                desc: "Preencha o cadastro e aguarde a aprovação.",
              },
              {
                step: "02",
                title: "Pague R$ 30 por mês",
                desc: "Pix ou cartão. Sem letras miúdas.",
              },
              {
                step: "03",
                title: "Acesse os benefícios",
                desc: "Marque consultas com valores especiais.",
              },
            ].map((it) => (
              <div
                key={it.step}
                className="rounded-2xl border border-neutral-200 bg-white p-6 hover:-translate-y-1 transition-transform"
                data-testid={`how-step-${it.step}`}
              >
                <p className="font-display text-3xl font-extrabold text-[#166534]/20">{it.step}</p>
                <p className="font-semibold mt-2">{it.title}</p>
                <p className="text-sm text-neutral-600 mt-1 leading-relaxed">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HIGHLIGHT */}
      <section className="bg-[#052E16] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Stethoscope className="w-7 h-7 text-emerald-300" />
            <h2 className="font-display mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight">
              Psicologia acessível. Para toda a família.
            </h2>
            <p className="mt-4 text-emerald-100/90 leading-relaxed max-w-lg">
              Atendimentos presenciais e online com psicólogos parceiros a valores especiais para
              associados. Cuidar da saúde emocional não pode ser um privilégio.
            </p>
            <Link to="/beneficios">
              <Button
                data-testid="highlight-cta"
                className="mt-6 rounded-full bg-white text-[#052E16] hover:bg-emerald-50 hover:text-[#052E16] px-6 h-11"
              >
                Conhecer os benefícios
              </Button>
            </Link>
          </div>
          <ul className="space-y-3">
            {[
              "Atendimento adulto, infantil e para idosos",
              "Terapia de casal e familiar",
              "Avaliação psicológica",
              "Agendamento flexível, presencial ou online",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3 text-emerald-50">
                <CheckCircle2 className="w-5 h-5 text-emerald-300 mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="rounded-3xl border border-neutral-200 bg-gradient-to-br from-[#F0FDF4] to-white p-8 md:p-14 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            Pronto para ressignificar o cuidado?
          </h2>
          <p className="mt-3 text-neutral-700 max-w-xl mx-auto">
            Comece agora. Preencha o cadastro em menos de 2 minutos.
          </p>
          <Link to="/adesao">
            <Button
              data-testid="footer-cta-signup"
              className="mt-6 rounded-full bg-[#166534] hover:bg-[#14532d] hover:text-white text-white px-7 h-12 text-base"
            >
              Fazer adesão agora
              <ArrowRight className="ml-1 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
